/**
 * Abdullah Al Salem University (AASU) — جامعة عبدالله السالم
 * Source: AASU undergraduate-admissions page (2026/2027 plan), PDF mirrored at
 * equalvant_rate/abdullah_al_salem/2026/aasu_admission_2026.pdf
 *
 * Four colleges. Apply-floor is 70% HS for any track (enforced via
 * getTrackMinHighSchool in the registry). College-level minHighSchool is the
 * competitive threshold — a soft warning if a student is below it but above 70%.
 *
 * Weights mirror the official "Comparative Percentage" formulas from the PDF:
 *   - Business Admin & Entrepreneurship : 70% HS + 15% English + 15% Math
 *   - Computer Engineering & Systems    : 65% HS + 15% English + 20% Math
 *   - Engineering & Energy              : 65% HS + 15% English + 20% Math
 *   - Medicine & Health Sciences (MD)   : 75% HS + 15% English + 10% Math
 *
 * Track support:
 *   - Business Admin accepts BOTH scientific (≥80%) and literary (≥85%).
 *   - Computing, Engineering, Medicine are scientific-track only.
 */

import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";

export const collegeWeights: Record<string, CollegeWeights> = {
    business_admin: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 15,
        minHighSchool: 80,
        minHighSchoolLiterary: 85,
        minEnglish: null,
        admissionType: "equivalent",
    },
    computing_systems: {
        highSchoolWeight: 65,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 20,
        englishWeight: 15,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    engineering_energy: {
        highSchoolWeight: 65,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 20,
        englishWeight: 15,
        minHighSchool: 80,
        minEnglish: null,
        admissionType: "equivalent",
    },
    medicine_health: {
        highSchoolWeight: 75,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 10,
        englishWeight: 15,
        minHighSchool: 90,
        minEnglish: null,
        additionalConditions: "mmi_interview",
        admissionType: "equivalent",
    },
};

// `equivalentRate` is the apply-floor (qualification gate). The published
// "lowest EQUIVALENT rate accepted" (أقل معدل مكافئ) from the official
// الحدود الدنيا للمعدل المكافئ table — semester 1, 2026/2027 — is stored in
// `minAcceptedLastYear` (single value; the table is track-agnostic except
// Entrepreneurship & Innovation, which publishes علمي/أدبي rows and keeps
// `minAcceptedLastYearByTrack`). Medicine's 92.27 is the DIFFERENTIAL rate
// (المعدل التفاضلي) computed after all admission requirements incl. the MMI
// exam — its display label says so.
export const departments: Department[] = [
    // ── Business Administration & Entrepreneurship (both tracks) ─────
    { code: "AASU-BA01", collegeKey: "business_admin", departmentKey: "entrepreneurship_innovation", equivalentRate: 80, type: "equivalent", minAcceptedLastYearByTrack: { scientific: 66.4, literary: 65.67 } },
    { code: "AASU-BA02", collegeKey: "business_admin", departmentKey: "digital_marketing",          equivalentRate: 80, type: "equivalent", minAcceptedLastYear: 59.23 },
    { code: "AASU-BA03", collegeKey: "business_admin", departmentKey: "supply_chain_logistics",     equivalentRate: 80, type: "equivalent", minAcceptedLastYear: 64.43 },

    // ── Computer Engineering & Systems (scientific only) ─────────────
    { code: "AASU-CS01", collegeKey: "computing_systems", departmentKey: "computer_systems_engineering", equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 72.63 },
    { code: "AASU-CS02", collegeKey: "computing_systems", departmentKey: "software_engineering",         equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 70.77 },
    { code: "AASU-CS03", collegeKey: "computing_systems", departmentKey: "cybersecurity_engineering",    equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 83.59 },
    { code: "AASU-CS04", collegeKey: "computing_systems", departmentKey: "data_science_ai",              equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 67.83 },

    // ── Engineering & Energy (scientific only) ───────────────────────
    { code: "AASU-EE01", collegeKey: "engineering_energy", departmentKey: "agricultural_engineering",                 equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 53.95 },
    { code: "AASU-EE02", collegeKey: "engineering_energy", departmentKey: "biomedical_engineering",                   equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 78.43 },
    { code: "AASU-EE03", collegeKey: "engineering_energy", departmentKey: "energy_systems",                           equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 69.92 },
    { code: "AASU-EE04", collegeKey: "engineering_energy", departmentKey: "environmental_sustainability_engineering", equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 52.85 },
    { code: "AASU-EE05", collegeKey: "engineering_energy", departmentKey: "materials_engineering",                    equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 54.59 },
    { code: "AASU-EE06", collegeKey: "engineering_energy", departmentKey: "robotics_mechatronics",                    equivalentRate: 80, type: "equivalent", track: "scientific", minAcceptedLastYear: 67.95 },

    // ── Medicine & Health Sciences (MD — single program, no specialty step) ─
    { code: "AASU-MH01", collegeKey: "medicine_health", departmentKey: null, equivalentRate: 90, type: "equivalent", track: "scientific", minAcceptedLastYear: 92.27 },
];

/**
 * Returns the AASU "lowest equivalent rate accepted last year" (أقل معدل
 * مكافئ — S1 2026/2027 table) for a specialty+track. The per-track values
 * exist only where the table splits by track (Entrepreneurship); otherwise
 * the single published value applies to both tracks, with the apply-floor
 * as a final fallback.
 */
export function getAasuMinAcceptedLastYear(
    dept: Department,
    track: Track | null | undefined,
    weights: CollegeWeights,
): number {
    if (track === "literary") {
        return (
            dept.minAcceptedLastYearByTrack?.literary
            ?? dept.minAcceptedLastYear
            ?? weights.minHighSchoolLiterary
            ?? weights.minHighSchool
            ?? 0
        );
    }
    return (
        dept.minAcceptedLastYearByTrack?.scientific
        ?? dept.minAcceptedLastYear
        ?? weights.minHighSchool
        ?? 0
    );
}

export const colleges: College[] = [
    {
        key: "business_admin",
        emoji: "💼",
        weights: collegeWeights.business_admin,
        departments: departments.filter((d) => d.collegeKey === "business_admin"),
    },
    {
        key: "computing_systems",
        emoji: "💻",
        track: "scientific",
        weights: collegeWeights.computing_systems,
        departments: departments.filter((d) => d.collegeKey === "computing_systems"),
    },
    {
        key: "engineering_energy",
        emoji: "⚡",
        track: "scientific",
        weights: collegeWeights.engineering_energy,
        departments: departments.filter((d) => d.collegeKey === "engineering_energy"),
    },
    {
        key: "medicine_health",
        emoji: "🩺",
        track: "scientific",
        weights: collegeWeights.medicine_health,
        departments: departments.filter((d) => d.collegeKey === "medicine_health"),
    },
];

export function getCollegeMinRate(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): number {
    const collegeDepts = departments.filter((d) => d.collegeKey === collegeKey);
    if (collegeDepts.length === 0) return 0;
    return Math.min(...collegeDepts.map((d) => d.equivalentRate));
}

export function getRequiredFields(weights: CollegeWeights): string[] {
    const fields: string[] = ["high_school"];
    if (weights.englishWeight > 0) fields.push("english");
    if (weights.mathWeight > 0) fields.push("math");
    if (weights.arabicWeight > 0) fields.push("arabic");
    if (weights.chemistryWeight > 0) fields.push("chemistry");
    return fields;
}

export function getCollegeSpecialties(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): Department[] {
    return departments.filter(
        (dept, index, self) =>
            dept.collegeKey === collegeKey &&
            dept.departmentKey !== null &&
            self.findIndex(
                (d) => d.departmentKey === dept.departmentKey && d.collegeKey === collegeKey,
            ) === index,
    );
}

export function hasSelectableSpecialties(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): boolean {
    return getCollegeSpecialties(collegeKey).length > 1;
}

/* ── AASU equivalent-rate calculation (isolated; PDF 2026/2027) ──────────
 * Each university owns its own formula here so a future divergence in
 * any college (e.g. Medicine vs Business Admin) can never leak into
 * another university's math.
 */
export function calculateEquivalentRate(weights: CollegeWeights, scores: Scores): number {
    if (weights.admissionType === "high_school") {
        return scores.high_school;
    }
    const rate =
        (scores.high_school * weights.highSchoolWeight) / 100 +
        ((scores.english || 0) * weights.englishWeight) / 100 +
        ((scores.math || 0) * weights.mathWeight) / 100 +
        ((scores.arabic || 0) * weights.arabicWeight) / 100 +
        ((scores.chemistry || 0) * weights.chemistryWeight) / 100;
    return Math.round(rate * 100) / 100;
}

export function meetsMinHighSchool(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minHighSchool === null) return true;
    return scores.high_school >= weights.minHighSchool;
}

export function meetsMinEnglish(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minEnglish === null) return true;
    return (scores.english || 0) >= weights.minEnglish;
}

/* ── Per-section public API (consumed via the registry) ─────────────────
 * AASU owns its track percentages, displayed-rate logic, and label key.
 */

/** AASU track minimum HS — lowest-accepting college on each track is
 *  Business Admin (80% scientific, 85% literary). */
export function getTrackMinHighSchool(track: Track | null | undefined): number | null {
    if (!track) return null;
    return track === "literary" ? 85 : 80;
}

/** AASU enforces every college's minHighSchool as a hard apply-floor — a
 *  student below the college's published minimum cannot proceed past the
 *  score-entry step, even if their track-min check passes. (KU and IM
 *  instead treat the track-min as the only hard gate and the college-min
 *  as competitive guidance — they return false from this function.) */
export function isCollegeMinHardGate(): boolean {
    return true;
}

/** Qualified-colleges filter gates on the published apply-floor. */
export function getDepartmentThreshold(dept: Department): number {
    return dept.equivalentRate;
}

/** AASU surfaces last-year's lowest-accepted EQUIVALENT rate per specialty
 *  on the results card (apply-floor is still the gate). Medicine has no
 *  specialty step (dept arrives null), so its single MD row — carrying the
 *  published differential rate — is resolved here via the MMI weights
 *  template. */
export function getDisplayedMinRate(
    dept: Department | null,
    track: Track | null | undefined,
    weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    if (!dept) {
        if (weights.additionalConditions === "mmi_interview") {
            const medicine = departments.find((d) => d.collegeKey === "medicine_health");
            if (medicine?.minAcceptedLastYear !== undefined) {
                return medicine.minAcceptedLastYear;
            }
        }
        return fallbackMinRate;
    }
    return getAasuMinAcceptedLastYear(dept, track, weights);
}

/** AASU's displayed value is an equivalent rate (أقل معدل مكافئ). Medicine's
 *  published figure is the differential rate incl. the MMI exam — its label
 *  says so (the MMI weights template is the discriminator). */
export function getDisplayedMinRateLabelKey(weights: CollegeWeights): string {
    return weights.additionalConditions === "mmi_interview"
        ? "dream_university_aasu_medicine_last_year_differential_rate"
        : "dream_university_aasu_last_year_equivalent_rate";
}

/** Medicine & Health Sciences shows an info box stating the apply-floor it
 *  gates on. Other AASU colleges have no score-step info box. */
export function getScoreInfoAlert(
    college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    collegeMinHs: number | null,
    _enteredHs: number,
): StepAlert[] | null {
    if (college.key !== "medicine_health" || collegeMinHs == null) {
        return null;
    }
    return [
        {
            severity: "info",
            titleKey: "dream_university_aasu_min_apply_alert_title",
            lines: [
                { key: "dream_university_aasu_min_apply_alert_body", params: { percent: collegeMinHs } },
            ],
        },
        {
            severity: "tip",
            iconKey: "list-checks",
            titleKey: "dream_university_aasu_medicine_mmi_title",
            lines: [
                { key: "dream_university_aasu_medicine_mmi_intro" },
                { key: "dream_university_aasu_medicine_mmi_point1" },
                { key: "dream_university_aasu_medicine_mmi_point2" },
            ],
        },
    ];
}

/** Specialty-step info alert. The S1 2026/2027 الحدود الدنيا table published
 *  the lowest accepted equivalent rate for every specialty, so the old
 *  "not announced" note is gone — each specialty card now carries its own
 *  published value instead. */
export function getSpecialtyInfoAlert(_college: College): StepAlert | null {
    return null;
}

/** Results-step failure lines. Medicine surfaces its own apply-rate wording. */
export function getResultsFailureLines(
    college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    const isMedicine = college.key === "medicine_health";
    return [
        {
            key: "dream_university_below_accepted_rate_failure_line1",
            params: { minHs: college.weights.minHighSchool ?? trackMinHs ?? 0 },
        },
        {
            key: isMedicine
                ? "dream_university_aasu_medicine_failure_apply_rate"
                : "dream_university_below_accepted_rate_failure_line2",
            params: { acceptedRate: minRate },
        },
    ];
}

export function getColleges(_gender?: Gender, _track?: Track | null): College[] {
    return colleges;
}

export function getDepartmentsForFilters(_gender?: Gender, _track?: Track | null): Department[] {
    return departments;
}
